import type { KibanaLogger } from '@revelio/iso-utility';

interface WebGLDiagnostics {
  contextType: 'webgl2' | 'webgl' | 'experimental-webgl' | 'none';
  vendor: string;
  renderer: string;
  hasDebugRendererInfo: boolean;
  errorMessage?: string;
}

function getWebGLDiagnostics(): WebGLDiagnostics {
  const canvas = document.createElement('canvas');
  const contextTypes = ['webgl2', 'webgl', 'experimental-webgl'] as const;
  let creationErrorMessage = '';

  const onCreationError = (e: Event) => {
    const raw =
      'statusMessage' in e
        ? (e as { statusMessage: unknown }).statusMessage
        : undefined;
    const msg = typeof raw === 'string' ? raw : undefined;
    if (msg && !creationErrorMessage) {
      creationErrorMessage = msg;
    }
  };

  canvas.addEventListener('webglcontextcreationerror', onCreationError);

  for (const contextType of contextTypes) {
    const gl = canvas.getContext(contextType) as WebGLRenderingContext | null;
    if (gl) {
      canvas.removeEventListener('webglcontextcreationerror', onCreationError);
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');

      const vendor = debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL)
        : gl.getParameter(gl.VENDOR);

      const renderer = debugInfo
        ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL)
        : gl.getParameter(gl.RENDERER);

      return {
        contextType,
        vendor: vendor ? String(vendor) : 'unknown',
        renderer: renderer ? String(renderer) : 'unknown',
        hasDebugRendererInfo: !!debugInfo,
      };
    }
  }

  canvas.removeEventListener('webglcontextcreationerror', onCreationError);

  return {
    contextType: 'none',
    vendor: 'unknown',
    renderer: 'unknown',
    hasDebugRendererInfo: false,
    errorMessage:
      creationErrorMessage ||
      'WebGL is not supported or hardware acceleration is disabled.',
  };
}

export function logWebGLInitFailure(
  kibanaLogger: KibanaLogger | undefined,
  error: unknown
): void {
  const diag = getWebGLDiagnostics();
  const errorMessage = error instanceof Error ? error.message : String(error);
  const reasonStr = diag.errorMessage ? ` | reason: ${diag.errorMessage}` : '';
  const message = `WebGL initialization failed: ${errorMessage} | contextType: ${diag.contextType} | vendor: ${diag.vendor} | renderer: ${diag.renderer} | hasDebugRendererInfo: ${diag.hasDebugRendererInfo}${reasonStr}`;
  const logOptions = {
    context: 'GenericMap.WebGLInit',
    url: window.location.href,
  };

  if (diag.contextType === 'none') {
    kibanaLogger?.warn(message, logOptions);
  } else {
    kibanaLogger?.error(message, logOptions);
  }
}
